/// <amd-module name="TFS/Admin/Common/DataAccessLayers/GraphDAL" />
import { effects } from "redux-saga";
import { aadPrefix, IEntityType } from "TFS/Admin/Common/Constants/Constants";
import { Avatar } from "TFS/Admin/Common/Contracts/VSS.Profile";
import * as AdminCommonResources from "TFS/Admin/Common/Resources";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { CleanErrorMessage, ParseErrorFromAPI } from "TFS/Admin/Common/Util/CustomErrorHelper";
import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { WebApiTeam } from "Tfs/Platform/Contracts/Core";
import { ITfsPageService } from "Tfs/Platform/Page";
import { CoreClientName, ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { format, startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import {
    GraphGroup,
    GraphGroupOriginIdCreationContext,
    GraphServicePrincipal,
    GraphServicePrincipalOriginIdCreationContext,
    GraphTraversalDirection,
    GraphUser,
    GraphUserCreationContext,
    GraphUserOriginIdCreationContext,
    GraphUserPrincipalNameCreationContext
} from "VSS/Platform/Contracts/Graph";
import { WrappedException } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IIdentity } from "VSSUI/IdentityPicker";

const MemberNotActiveException = "MemberNotActiveException";
/**
 * Helper function to fetch a user
 * @param descriptor descriptor of the user to retrieve
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export function* fetchUserByDescriptor(descriptor: string, telemetryLogger: AdminFeatureTelemetryLogger): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        return yield effects.call([graphClient, graphClient.getUser], descriptor);
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        throw error;
    }
}

/**
 * Helper function to fetch a service principal
 * @param descriptor descriptor of the service principal to retrieve
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
 export function* fetchServicePrincipalByDescriptor(descriptor: string, telemetryLogger: AdminFeatureTelemetryLogger): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        return yield effects.call([graphClient, graphClient.getServicePrincipal], descriptor);
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        throw error;
    }
}

/**
 * Helper function to fetch a group
 * @param descriptor descriptor of the group to retrieve
 * @param telemetryLogger For telemetry purposes; telemetry helper class for this view
 */
export function* fetchGroupByDescriptor(descriptor: string, telemetryLogger: AdminFeatureTelemetryLogger): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        return yield effects.call([graphClient, graphClient.getGroup], descriptor);
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        throw error;
    }
}

/**
 * Helper function to get all the memberships where this descriptor is a member in the relationship
 * @param subjectDescriptor descriptor of the group to retrieve
 * @param direction - Defaults to Up.
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export function* listMembership(subjectDescriptor: string, telemetryLogger: AdminFeatureTelemetryLogger, direction?: GraphTraversalDirection): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        return yield effects.call([graphClient, graphClient.listMemberships], subjectDescriptor, direction);
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, ParseErrorFromAPI(error));
        throw error;
    }
}

/**
 * Helper function that deletes a membership between a container and subject.
 *
 * @param subjectDescriptor - A descriptor to a group or user that is the child subject in the relationship.
 * @param containerDescriptor - A descriptor to a group that is the container in the relationship.
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export function* removeMembership(subjectDescriptor: string, containerDescriptor: string, telemetryLogger: AdminFeatureTelemetryLogger): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        return yield effects.call([graphClient, graphClient.removeMembership], subjectDescriptor, containerDescriptor);
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        throw error;
    }
}

export interface IAddGroupMembershipIdentity {
    displayName: string;
    subjectDescriptor: string;
}

/**
 * Helper function to create a new group
 * @param parentGroup Container group
 * @param memberDescriptor Member group to be added
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export async function addGroupMembership(
    pageContext: IVssPageContext, parentGroup: IAddGroupMembershipIdentity, member: IAddGroupMembershipIdentity, telemetryLogger: AdminFeatureTelemetryLogger) {

    if (parentGroup.subjectDescriptor === member.subjectDescriptor) {
        throw "Invalid data"
    }
    const graphClient: IGraphRestClient = pageContext.getRestClient<IGraphRestClient>(GraphClientName);
    const result = await graphClient.addMembership(member.subjectDescriptor, parentGroup.subjectDescriptor);
    const everyoneGroupSubjectDescriptor = await getWellKnownGroupEveryoneSubjectDescriptor(pageContext);
    telemetryLogger.captureTelemetry({
        MemberDescriptor: member.subjectDescriptor,
        ParentDescriptor: parentGroup.subjectDescriptor,
    });
    // Check if the member is everyone group and throws an error since API skips the check
    if (everyoneGroupSubjectDescriptor) {
        if (everyoneGroupSubjectDescriptor === member.subjectDescriptor) {
            const errorMessage = format(AdminCommonResources.ErrorMessage_CyclicGroupContainment, parentGroup.displayName, member.displayName);
            const error = new Error(errorMessage);
            (error as any).serverError = {
                typeKey: "AddMemberCyclicMembershipException",
                message: errorMessage
            } as WrappedException;
            throw error;
        }
    }
    return result;
}

async function getWellKnownGroupEveryoneSubjectDescriptor(pageContext: IVssPageContext): Promise<string | undefined> {
    const contributionId = "ms.vss-admin-web.admin-well-known-group-everyone-data-provider";
    try {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        return await contributionService.getDataAsync<string>(contributionId);
    } catch {
        return undefined;
    }
}

/**
 * Helper function to create a new user
 * @param subjectIdentity Member to be added
 * @param graphGroup Container group
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
function* createUser(subjectIdentity: IIdentity, graphGroup: GraphGroup, telemetryLogger: AdminFeatureTelemetryLogger) {
    const pageContext: IVssPageContext = yield effects.getContext("pageContext");

    if (subjectIdentity && graphGroup) {
        const graphClient: IGraphRestClient = yield effects.call([pageContext, pageContext.getRestClient], GraphClientName);
        try {
            var userCreationContext: GraphUserCreationContext;

            if (subjectIdentity.originDirectory === IEntityType.Custom) {
                const userPrincipalNameCreationContext: GraphUserPrincipalNameCreationContext = {
                    storageKey: "",
                    principalName: subjectIdentity.entityId
                };
                userCreationContext = userPrincipalNameCreationContext;
            } else {
                const userOriginIdCreationContext: GraphUserOriginIdCreationContext = {
                    storageKey: "",
                    originId: subjectIdentity.originId,
                    origin: subjectIdentity.originDirectory
                };
                userCreationContext = userOriginIdCreationContext;
            }
            var user: GraphUser = yield effects.call([graphClient, graphClient.createUser], userCreationContext, [graphGroup.descriptor]);
            yield effects.call(telemetryLogger.captureTelemetry, {
                subjectDescriptor: subjectIdentity.subjectDescriptor,
                groupDescriptor: graphGroup.descriptor
            });
            return user;
        } catch (error) {
            if (error && error.message && startsWith(error.message, "400:")) {
                return (yield effects.call(
                    _callCreateUserWithSignInAddress,
                    graphClient,
                    telemetryLogger,
                    subjectIdentity.signInAddress || "",
                    graphGroup.descriptor,
                    subjectIdentity.originId
                )) as GraphUser;
            } else {
                yield effects.call(telemetryLogger.captureTelemetry, {
                    OriginId: subjectIdentity.originId,
                    Descriptor: graphGroup.descriptor,
                    ErrorMessage: ParseErrorFromAPI(error)
                });
                throw error;
            }
        }
    } else {
        yield effects.call(telemetryLogger.captureTelemetry, {
            ErrorMessage: "Invalid data",
            SubjectIdentity: subjectIdentity ? subjectIdentity.description : "undefined",
            GraphGroup: graphGroup ? graphGroup.descriptor : "undefined"
        });

        throw "Invalid data";
    }
}

/**
 * Helper function to create a new service principal
 * @param subjectIdentity Member to be added
 * @param graphGroup Container group
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
function* createServicePrincipal(subjectIdentity: IIdentity, graphGroup: GraphGroup, telemetryLogger: AdminFeatureTelemetryLogger) {
    const pageContext: IVssPageContext = yield effects.getContext("pageContext");

    if (subjectIdentity && graphGroup) {
        const graphClient: IGraphRestClient = yield effects.call([pageContext, pageContext.getRestClient], GraphClientName);
        try {
            var servicePrincipalCreationContext: GraphServicePrincipalOriginIdCreationContext =
            {
                storageKey: "",
                originId: subjectIdentity.originId
            };
            
            var servicePrincipal: GraphServicePrincipal = yield effects.call([graphClient, graphClient.createServicePrincipal], servicePrincipalCreationContext, [graphGroup.descriptor]);
            yield effects.call(telemetryLogger.captureTelemetry, {
                subjectDescriptor: subjectIdentity.subjectDescriptor,
                groupDescriptor: graphGroup.descriptor
            });
            return servicePrincipal;
        } catch (error) {
            yield effects.call(telemetryLogger.captureTelemetry, {
                OriginId: subjectIdentity.originId,
                Descriptor: graphGroup.descriptor,
                ErrorMessage: ParseErrorFromAPI(error)
            });
            throw error;
        }
    } else {
        yield effects.call(telemetryLogger.captureTelemetry, {
            ErrorMessage: "Invalid data",
            SubjectIdentity: subjectIdentity ? subjectIdentity.description : "undefined",
            GraphGroup: graphGroup ? graphGroup.descriptor : "undefined"
        });

        throw "Invalid data";
    }
}

function* _callCreateUserWithSignInAddress(
    graphClient: IGraphRestClient,
    telemetryLogger: AdminFeatureTelemetryLogger,
    signInAddress: string,
    containerDescriptor: string,
    UserOriginId: string
) {
    const signInAddressCreateUserTelemetryLogger = telemetryLogger.getTelemetryLoggerForNewAction("Create user using SignInAddress");
    yield effects.call(signInAddressCreateUserTelemetryLogger.captureAttemptStart);
    try {
        const principalNameCreationContext: GraphUserPrincipalNameCreationContext = {
            principalName: signInAddress,
            storageKey: ""
        };
        return (yield effects.call([graphClient, graphClient.createUser], principalNameCreationContext, [containerDescriptor])) as GraphUser;
    } catch (error) {
        yield effects.call(signInAddressCreateUserTelemetryLogger.captureTelemetry, {
            Descriptor: containerDescriptor,
            originIdForsignInAddress: UserOriginId,
            ErrorMessage: ParseErrorFromAPI(error)
        });
        throw error;
    }
}

/**
 * Helper function to create a new group
 * @param subjectIdentity Member to be added
 * @param graphGroup Container group
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
function* createGroup(
    subjectIdentity: IIdentity,
    graphGroup: GraphGroup,
    telemetryLogger: AdminFeatureTelemetryLogger,
    scopeDescriptor?: string | undefined
) {
    const pageContext: IVssPageContext = yield effects.getContext("pageContext");

    if (subjectIdentity && graphGroup) {
        try {
            const graphClient: IGraphRestClient = yield effects.call([pageContext, pageContext.getRestClient], GraphClientName);
            const creationContext: GraphGroupOriginIdCreationContext = {
                originId: subjectIdentity.originId,
                storageKey: ""
            };
            yield effects.call(telemetryLogger.captureTelemetry, {
                subjectDescriptor: subjectIdentity.subjectDescriptor,
                groupDescriptor: graphGroup.descriptor
            });
            return (yield effects.call([graphClient, graphClient.createGroup], creationContext, scopeDescriptor, [graphGroup.descriptor])) as GraphGroup;
        } catch (error) {
            yield effects.call(telemetryLogger.captureTelemetry, {
                OriginId: subjectIdentity.originId,
                Descriptor: graphGroup.descriptor,
                ErrorMessage: ParseErrorFromAPI(error)
            });
            throw error;
        }
    } else {
        yield effects.call(telemetryLogger.captureTelemetry, {
            ErrorMessage: "Invalid data",
            SubjectIdentity: subjectIdentity ? subjectIdentity.description : "undefined",
            GraphGroup: graphGroup ? graphGroup.descriptor : "undefined"
        });

        throw "Invalid data";
    }
}

export interface IAddMembersToGroupResult {
    success: Array<{
        subjectIdentity: IIdentity;
        memberDescriptor: string | undefined;
    }>;
    failed: Array<{
        subjectIdentity: IIdentity;
    }>;
    errors: any[];
}

/**
 * Helper function to add a group or user as member of a group
 * @param subjectIdentity Member to be added
 * @param graphGroup Container group
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export function* addMembersToGroup(
    subjectIdentities: IIdentity[],
    graphGroup: GraphGroup,
    supportServicePrincipals: boolean,
    telemetryLogger: AdminFeatureTelemetryLogger,
    scopeDescriptor?: string | undefined
): Iterator<unknown, IAddMembersToGroupResult> {
    if (subjectIdentities.length === 0 || !graphGroup) {
        yield effects.call(telemetryLogger.captureTelemetry, {
            ErrorMessage: "Invalid data",
            SubjectIdentities: subjectIdentities.length > 0 ? subjectIdentities.length : "empty",
            GraphGroup: graphGroup ? graphGroup.descriptor : "undefined"
        });
    }

    const success: IAddMembersToGroupResult["success"] = [];
    const failed: IAddMembersToGroupResult["failed"] = [];
    const errors: any[] = [];
    yield effects.all(
        subjectIdentities.map((subjectIdentity) =>
            effects.call(function* () {
                try {
                    if (subjectIdentity.active === false) {
                        yield effects.call(telemetryLogger.captureError, "Member not active");
                        failed.push({ subjectIdentity });
                        errors.push({ serverError: { typeKey: MemberNotActiveException } });
                        return;
                    }

                    let memberDescriptor: string | undefined;
                    if (subjectIdentity.entityType === "User" || subjectIdentity.entityType === IEntityType.Custom) {
                        const user: GraphUser = yield effects.call(createUser, subjectIdentity, graphGroup, telemetryLogger);
                        memberDescriptor = user.descriptor;
                    } else if (supportServicePrincipals && subjectIdentity.entityType === "ServicePrincipal"){
                        const servicePrincipal: GraphServicePrincipal = yield effects.call(createServicePrincipal, subjectIdentity, graphGroup, telemetryLogger);
                        memberDescriptor = servicePrincipal.descriptor;
                    } else {
                        var addGroupToParentTelemetryLogger: AdminFeatureTelemetryLogger = telemetryLogger.getTelemetryLoggerForNewAction(
                            "Add group to parent group"
                        ); // This is just an informational capture
                        yield effects.call(addGroupToParentTelemetryLogger.captureTelemetry, {
                            groupDescriptor: graphGroup ? graphGroup.descriptor : ""
                        });

                        memberDescriptor = subjectIdentity.subjectDescriptor;
                        let memberDisplayName = subjectIdentity.displayName;
                        // If the member to add is an AAD group, we need to create it in the Org and add it to the parent group.
                        if (subjectIdentity.originDirectory === aadPrefix) {
                            const orgGroup: GraphGroup = yield effects.call(createGroup, subjectIdentity, graphGroup, telemetryLogger);
                            memberDescriptor = orgGroup.descriptor;
                            memberDisplayName = orgGroup.displayName;
                        }

                        const pageContext: IVssPageContext = (yield effects.getContext("pageContext"));
                        yield effects.call(addGroupMembership, pageContext, {
                            displayName: graphGroup.displayName,
                            subjectDescriptor: graphGroup.descriptor,
                        }, {
                            displayName: memberDisplayName,
                            subjectDescriptor: memberDescriptor,
                        }, telemetryLogger);
                    }

                    success.push({ subjectIdentity, memberDescriptor });
                } catch (error) {
                    yield effects.call(telemetryLogger.captureError, CleanErrorMessage(error.message || error));
                    failed.push({ subjectIdentity });
                    errors.push(error);
                }
            })
        )
    );
    return { success, failed, errors };
}

/**
 * Helper function to check if there is a membership relationship with a member and a group
 * @param memberDescriptor descriptor of the member
 * @param groupDescriptor descriptor of the group
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export function* checkMembershipExistence(memberDescriptor: string, groupDescriptor: string, telemetryLogger: AdminFeatureTelemetryLogger): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    if (memberDescriptor === "" || groupDescriptor === "") {
        yield effects.call(telemetryLogger.captureTelemetry, {
            ErrorMessage: "Invalid data"
        });
    }

    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    try {
        return yield effects.call([graphClient, graphClient.checkMembershipExistence], memberDescriptor, groupDescriptor);
    } catch (error) {
        yield effects.call(telemetryLogger.captureTelemetry, {
            memberDescriptor: memberDescriptor,
            groupDescriptor: groupDescriptor,
            ErrorMessage: error.message || error
        });

        return false;
    }
}

/**
 * Helper function to create a team and parse it to a GraphGroup
 * @param description description of the team to create
 * @param displayName name of the team to create
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export function* createTeam(description: string, displayName: string, telemetryLogger: AdminFeatureTelemetryLogger): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const restClient: ICoreRestClient = (yield effects.call([pageContext, pageContext.getRestClient], CoreClientName)) as any;
    const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
    const pageData = pageService.getData();
    const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

    if (pageData && pageData.project) {
        const team = {
            description: description,
            projectId: pageData.project.id,
            name: displayName,
            identity: {
                customDisplayName: displayName
            }
        } as WebApiTeam;

        // Create the team
        const createdTeam = (yield effects.call([restClient, restClient.createTeam], team, pageData.project.id)) as any;
        // Fetch the newly created team as a group
        return yield effects.call([graphClient, graphClient.getGroup], createdTeam.identity.subjectDescriptor);
    }
}

/**
 * Helper function to fetch team members with extended properties
 * This method is used to find the team admins
 * @param teamId The name or ID (GUID) of the team
 */
export function* getTeamMembers(teamId: string): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const restClient: ICoreRestClient = (yield effects.call([pageContext, pageContext.getRestClient], CoreClientName)) as any;

    const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
    const pageData = pageService.getData();

    if (pageData && pageData.project) {
        return yield effects.call([restClient, restClient.getTeamMembersWithExtendedProperties], pageData.project.id, teamId);
    } else {
        return [];
    }
}

/**
 * Helper function to set the group image
 *
 * @param subjectDescriptor - A descriptor to a group or user that is the child subject in the relationship.
 * @param avatar - Image object; new avatar for subject
 * @param telemetryLogger For telemetry purposes; includes component where this action was performed
 */
export function* setAtavar(subjectDescriptor: string, avatar: Avatar, telemetryLogger: AdminFeatureTelemetryLogger): any {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        return yield effects.call([graphClient, graphClient.setAvatar], avatar, subjectDescriptor);
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        throw error;
    }
}
